import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LogOut, Users, UserCheck, Clock, CheckCircle, XCircle, Trash2, BarChart3, KeyRound, AlertTriangle, Mail, FileText, Send } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import ContentManager from "@/components/ContentManager";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [pendingInterpreters, setPendingInterpreters] = useState([]);
  const [allInterpreters, setAllInterpreters] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [emailData, setEmailData] = useState({
    new_email: "",
    password: "",
  });
  
  // Bulk email state
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  const [bulkEmailTarget, setBulkEmailTarget] = useState(""); // "interpreters" or "users"
  const [bulkEmailData, setBulkEmailData] = useState({
    subject: "",
    message: "",
  });
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);

  // Use XMLHttpRequest to avoid body stream issues with external scripts
  const apiRequest = (url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      xhr.timeout = 15000;
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data: {} });
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.send(options.body || null);
    });
  };

  useEffect(() => {
    if (user && user.role === "admin") {
      fetchStats();
      fetchPendingInterpreters();
      fetchAllInterpreters();
      fetchAllUsers();
      fetchActivityLogs();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const result = await apiRequest(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result.ok) {
        setStats(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchPendingInterpreters = async () => {
    try {
      const result = await apiRequest(`${API}/admin/interpreters/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result.ok) {
        setPendingInterpreters(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch pending interpreters:", error);
    }
  };

  const fetchAllInterpreters = async () => {
    try {
      const result = await apiRequest(`${API}/admin/interpreters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result.ok) {
        setAllInterpreters(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch interpreters:", error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const result = await apiRequest(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result.ok) {
        setAllUsers(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const result = await apiRequest(`${API}/admin/activity-logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result.ok) {
        setActivityLogs(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
    }
  };

  const handleApprove = async (userId) => {
    try {
      const result = await apiRequest(`${API}/admin/interpreters/${userId}/approve`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (result.ok) {
        toast.success("Interpreter approved!");
        fetchPendingInterpreters();
        fetchAllInterpreters();
        fetchStats();
        fetchActivityLogs();
      } else {
        toast.error("Failed to approve interpreter");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleReject = async (userId) => {
    try {
      const result = await apiRequest(`${API}/admin/interpreters/${userId}/reject`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (result.ok) {
        toast.success("Interpreter rejected");
        fetchPendingInterpreters();
        fetchAllInterpreters();
        fetchStats();
        fetchActivityLogs();
      } else {
        toast.error("Failed to reject interpreter");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const result = await apiRequest(`${API}/admin/users/${deleteTarget.user_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (result.ok) {
        toast.success("User deleted successfully");
        setDeleteTarget(null);
        fetchAllInterpreters();
        fetchAllUsers();
        fetchStats();
        fetchActivityLogs();
      } else {
        toast.error("Failed to delete user");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    try {
      const result = await apiRequest(`${API}/admin/change-password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          old_password: passwordData.old_password,
          new_password: passwordData.new_password,
        }),
      });

      if (result.ok) {
        toast.success("Password changed successfully!");
        setShowPasswordDialog(false);
        setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
        fetchActivityLogs();
      } else {
        toast.error(result.data.detail || "Failed to change password");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    
    if (!emailData.new_email || !emailData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const result = await apiRequest(`${API}/admin/change-email`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          new_email: emailData.new_email,
          password: emailData.password,
        }),
      });

      if (result.ok) {
        toast.success("Email changed successfully! Please log in again with your new email.");
        setShowEmailDialog(false);
        setEmailData({ new_email: "", password: "" });
        fetchActivityLogs();
        // Log out user since token contains old email
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else {
        toast.error(result.data.detail || "Failed to change email");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleSendBulkEmail = async (e) => {
    e.preventDefault();
    
    if (!bulkEmailData.subject.trim() || !bulkEmailData.message.trim()) {
      toast.error("Please fill in both subject and message");
      return;
    }
    
    setSendingBulkEmail(true);
    try {
      const endpoint = bulkEmailTarget === "interpreters" 
        ? `${API}/admin/email/interpreters` 
        : `${API}/admin/email/users`;
      
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", endpoint, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.timeout = 120000; // 2 minutes for bulk email
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, data });
          } catch (e) {
            resolve({ ok: false, data: { detail: "Invalid response" } });
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.ontimeout = () => reject(new Error("Request timed out"));
        xhr.send(JSON.stringify(bulkEmailData));
      });

      if (result.ok) {
        toast.success(`Email sent to ${result.data.sent_count} ${bulkEmailTarget}!`);
        if (result.data.failed_count > 0) {
          toast.warning(`Failed to send to ${result.data.failed_count} recipients`);
        }
        setShowBulkEmailDialog(false);
        setBulkEmailData({ subject: "", message: "" });
        fetchActivityLogs();
      } else {
        toast.error(result.data.detail || "Failed to send emails");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setSendingBulkEmail(false);
    }
  };

  const openBulkEmailDialog = (target) => {
    setBulkEmailTarget(target);
    setBulkEmailData({ subject: "", message: "" });
    setShowBulkEmailDialog(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Admin Dashboard</h1>
            <p className="text-slate-300 text-sm mt-1" style={{ fontFamily: 'Public Sans' }}>System Management</p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="change-email-btn"
              onClick={() => setShowEmailDialog(true)}
              variant="ghost"
              className="text-white hover:text-slate-200 hover:bg-slate-800"
            >
              <Mail className="w-4 h-4 mr-2" />
              Change Email
            </Button>
            <Button
              data-testid="change-password-btn"
              onClick={() => setShowPasswordDialog(true)}
              variant="ghost"
              className="text-white hover:text-slate-200 hover:bg-slate-800"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Change Password
            </Button>
            <Button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              variant="ghost"
              className="text-white hover:text-slate-200 hover:bg-slate-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Password Expiry Warning */}
        {user?.password_expired && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <div>
                <h3 className="font-semibold text-red-900">Password Expired</h3>
                <p className="text-sm text-red-700 mt-1">
                  Your password has expired (passwords expire after 90 days). Please change your password immediately for security.
                </p>
                <Button
                  onClick={() => setShowPasswordDialog(true)}
                  size="sm"
                  className="mt-2 bg-red-600 hover:bg-red-700 text-white"
                >
                  Change Password Now
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-slate-600 font-medium">Total Users</p>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.total_users}</p>
            </div>
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-2">
                <UserCheck className="w-5 h-5 text-emerald-600" />
                <p className="text-sm text-slate-600 font-medium">Approved Interpreters</p>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.approved_interpreters}</p>
            </div>
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <p className="text-sm text-slate-600 font-medium">Pending Approval</p>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.pending_interpreters}</p>
            </div>
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                <p className="text-sm text-slate-600 font-medium">Total Bookings</p>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.total_bookings}</p>
            </div>
          </div>
        )}

        {/* Management Tabs */}
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="pending" data-testid="pending-tab">
              <Clock className="w-4 h-4 mr-2" />
              Pending ({pendingInterpreters.length})
            </TabsTrigger>
            <TabsTrigger value="interpreters" data-testid="interpreters-tab">
              <UserCheck className="w-4 h-4 mr-2" />
              Interpreters ({allInterpreters.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">
              <Users className="w-4 h-4 mr-2" />
              Users ({allUsers.filter(u => u.role === "user").length})
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="logs-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Logs ({activityLogs.length})
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="content-tab">
              <FileText className="w-4 h-4 mr-2" />
              Site Content
            </TabsTrigger>
          </TabsList>

          {/* Pending Interpreters Tab */}
          <TabsContent value="pending">
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>Pending Interpreter Approvals</h2>
              {pendingInterpreters.length === 0 ? (
                <p className="text-slate-600 text-center py-12">No pending approvals</p>
              ) : (
                <div className="space-y-4">
                  {pendingInterpreters.map((interpreter) => (
                    <div key={interpreter.user_id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{interpreter.full_name}</h3>
                          <p className="text-sm text-slate-600 mt-1">{interpreter.email}</p>
                          <div className="flex gap-2 mt-2">
                            {interpreter.languages.map((lang, idx) => (
                              <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                {lang}
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2 mt-2">
                            {interpreter.specializations.map((spec, idx) => (
                              <span key={idx} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                                {spec}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            data-testid={`approve-${interpreter.user_id}`}
                            onClick={() => handleApprove(interpreter.user_id)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            size="sm"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            data-testid={`reject-${interpreter.user_id}`}
                            onClick={() => handleReject(interpreter.user_id)}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            size="sm"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* All Interpreters Tab */}
          <TabsContent value="interpreters">
            <div className="card p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>All Interpreters</h2>
                <Button
                  data-testid="email-all-interpreters-btn"
                  onClick={() => openBulkEmailDialog("interpreters")}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Email All Interpreters
                </Button>
              </div>
              <div className="space-y-3">
                {allInterpreters.map((interpreter) => (
                  <div key={interpreter.user_id} className="border border-slate-200 rounded-lg p-4 flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-slate-900">{interpreter.full_name}</h3>
                      <p className="text-sm text-slate-600">{interpreter.email}</p>
                      <div className="flex gap-2 mt-1">
                        <span className={`status-badge status-${interpreter.approval_status}`}>
                          {interpreter.approval_status}
                        </span>
                        <span className="text-xs text-slate-500">
                          {interpreter.languages.join(", ")}
                        </span>
                      </div>
                    </div>
                    <Button
                      data-testid={`delete-interpreter-${interpreter.user_id}`}
                      onClick={() => setDeleteTarget(interpreter)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="users">
            <div className="card p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>All Users</h2>
                <Button
                  data-testid="email-all-users-btn"
                  onClick={() => openBulkEmailDialog("users")}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Email All Users
                </Button>
              </div>
              <div className="space-y-3">
                {allUsers.filter(u => u.role === "user").map((user) => (
                  <div key={user.user_id} className="border border-slate-200 rounded-lg p-4 flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-slate-900">{user.full_name}</h3>
                      <p className="text-sm text-slate-600">{user.email}</p>
                    </div>
                    <Button
                      data-testid={`delete-user-${user.user_id}`}
                      onClick={() => setDeleteTarget(user)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Activity Logs Tab */}
          <TabsContent value="logs">
            <div className="card p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Activity Logs</h2>
                <Button
                  data-testid="refresh-logs-btn"
                  onClick={fetchActivityLogs}
                  variant="outline"
                  size="sm"
                  className="border-slate-300"
                >
                  Refresh
                </Button>
              </div>
              {activityLogs.length === 0 ? (
                <p className="text-slate-600 text-center py-12">No activity logs yet</p>
              ) : (
                <div className="space-y-3">
                  {activityLogs.map((log) => {
                    const actionColors = {
                      approve_interpreter: "bg-emerald-50 border-emerald-200 text-emerald-800",
                      reject_interpreter: "bg-red-50 border-red-200 text-red-800",
                      delete_user: "bg-orange-50 border-orange-200 text-orange-800",
                      delete_interpreter: "bg-orange-50 border-orange-200 text-orange-800",
                      change_password: "bg-blue-50 border-blue-200 text-blue-800",
                    };
                    
                    const actionLabels = {
                      approve_interpreter: "Approved Interpreter",
                      reject_interpreter: "Rejected Interpreter",
                      delete_user: "Deleted User",
                      delete_interpreter: "Deleted Interpreter",
                      change_password: "Changed Password",
                    };
                    
                    const date = new Date(log.timestamp);
                    const formattedDate = date.toLocaleDateString() + " " + date.toLocaleTimeString();
                    
                    return (
                      <div key={log.log_id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs px-2 py-1 rounded border font-medium ${actionColors[log.action] || "bg-slate-50 border-slate-200 text-slate-800"}`}>
                                {actionLabels[log.action] || log.action}
                              </span>
                              <span className="text-xs text-slate-500">{formattedDate}</span>
                            </div>
                            <div className="text-sm">
                              <p className="text-slate-900">
                                <span className="font-semibold">{log.admin_email}</span> performed action on{" "}
                                <span className="font-semibold">{log.target_user_name}</span> ({log.target_user_email})
                              </p>
                              {log.details && (
                                <p className="text-slate-600 mt-1">{log.details}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Site Content Tab */}
          <TabsContent value="content">
            <div className="card p-6">
              <ContentManager />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteTarget?.full_name}? This action cannot be undone. All associated bookings and availability will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Update your admin password. Password must be strong and will expire after 90 days.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label htmlFor="old_password">Current Password</Label>
              <Input
                id="old_password"
                data-testid="old-password-input"
                type="password"
                required
                value={passwordData.old_password}
                onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                className="mt-2"
                placeholder="Enter current password"
              />
            </div>
            <div>
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                data-testid="new-password-input"
                type="password"
                required
                minLength={8}
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="mt-2"
                placeholder="Enter new password"
              />
              <PasswordStrengthMeter password={passwordData.new_password} />
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                data-testid="confirm-password-input"
                type="password"
                required
                minLength={8}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="mt-2"
                placeholder="Re-enter new password"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-xs text-blue-900">
                <strong>Password Policy:</strong> Passwords must contain at least 8 characters including uppercase, lowercase, number, and special character. Passwords expire after 90 days.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowPasswordDialog(false);
                  setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-password-change-btn"
                type="submit"
                className="flex-1 bg-slate-900 text-white hover:bg-slate-800"
              >
                Change Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Email Address</DialogTitle>
            <DialogDescription>
              Enter your new email address and confirm with your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangeEmail} className="space-y-4">
            <div>
              <Label className="text-sm text-slate-600">Current Email</Label>
              <p className="text-slate-900 font-medium mt-1">{user?.email}</p>
            </div>
            <div>
              <Label htmlFor="new_email">New Email Address</Label>
              <Input
                id="new_email"
                data-testid="new-email-input"
                type="email"
                required
                value={emailData.new_email}
                onChange={(e) => setEmailData({ ...emailData, new_email: e.target.value })}
                className="mt-2"
                placeholder="Enter new email address"
              />
            </div>
            <div>
              <Label htmlFor="email_password">Confirm with Password</Label>
              <Input
                id="email_password"
                data-testid="email-password-input"
                type="password"
                required
                value={emailData.password}
                onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
                className="mt-2"
                placeholder="Enter your password to confirm"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
              <p className="text-xs text-amber-900">
                <strong>Note:</strong> After changing your email, you will be logged out and need to log in again with your new email address.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEmailDialog(false);
                  setEmailData({ new_email: "", password: "" });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-email-change-btn"
                type="submit"
                className="flex-1 bg-slate-900 text-white hover:bg-slate-800"
              >
                Change Email
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Email Dialog */}
      <Dialog open={showBulkEmailDialog} onOpenChange={setShowBulkEmailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Email All {bulkEmailTarget === "interpreters" ? "Interpreters" : "Users"}
            </DialogTitle>
            <DialogDescription>
              This will send an email to all {bulkEmailTarget === "interpreters" ? "approved interpreters" : "registered users"}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendBulkEmail} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="bulk_email_subject">Subject *</Label>
              <Input
                id="bulk_email_subject"
                data-testid="bulk-email-subject"
                required
                value={bulkEmailData.subject}
                onChange={(e) => setBulkEmailData({ ...bulkEmailData, subject: e.target.value })}
                className="mt-2"
                placeholder="Enter email subject"
              />
            </div>
            <div>
              <Label htmlFor="bulk_email_message">Message *</Label>
              <Textarea
                id="bulk_email_message"
                data-testid="bulk-email-message"
                required
                value={bulkEmailData.message}
                onChange={(e) => setBulkEmailData({ ...bulkEmailData, message: e.target.value })}
                className="mt-2 min-h-[150px]"
                placeholder="Enter your message..."
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-xs text-blue-900">
                <strong>Note:</strong> The email will be sent to {bulkEmailTarget === "interpreters" 
                  ? `${allInterpreters.filter(i => i.approval_status === "approved").length} approved interpreters` 
                  : `${allUsers.filter(u => u.role === "user").length} users`}.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowBulkEmailDialog(false);
                  setBulkEmailData({ subject: "", message: "" });
                }}
                className="flex-1"
                disabled={sendingBulkEmail}
              >
                Cancel
              </Button>
              <Button
                data-testid="send-bulk-email-btn"
                type="submit"
                className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
                disabled={sendingBulkEmail}
              >
                {sendingBulkEmail ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;